import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
  ArrowRight,
  Zap,
  Shield,
  Clock,
  Bell,
  BarChart3,
  Activity,
  ChevronDown,
  Star,
  Check,
} from "lucide-react";

const features = [
  {
    icon: Zap,
    title: "Real-time Confluence",
    description:
      "Multiple technical factors combined into clear, actionable signals. Know exactly when price, volume, and momentum align.",
  },
  {
    icon: Shield,
    title: "No Repaint Signals",
    description:
      "What you see is what you get. Our signals never repaint or change after the candle closes.",
  },
  {
    icon: Clock,
    title: "Optimized for 15-Min",
    description:
      "Tested and optimized for the 15-minute timeframe. Get the most accurate confluence signals where price action and volume align perfectly.",
  },
  {
    icon: Bell,
    title: "Custom Alerts",
    description:
      "Set up webhook alerts for your trading bot or receive push notifications directly to your phone.",
  },
  {
    icon: BarChart3,
    title: "Support & Resistance",
    description:
      "Automatic detection of key S/R zones with confluence scoring. Know which levels matter most.",
  },
  {
    icon: Activity,
    title: "Volume Profile",
    description:
      "See where the smart money is positioned. Volume delta and cumulative volume analysis included.",
  },
];

const testimonials = [
  {
    quote: "Confluence changed my trading completely. The confluence signals are incredibly accurate.",
    name: "Alex M.",
    role: "Day Trader",
  },
  {
    quote: "Finally an indicator that doesn't repaint. I can trust the signals I see.",
    name: "Sarah K.",
    role: "Swing Trader",
  },
  {
    quote: "The multi-timeframe analysis saves me hours every day. Worth every penny.",
    name: "Mike R.",
    role: "Full-time Trader",
  },
  {
    quote: "Support is amazing and the Discord community is super helpful.",
    name: "James L.",
    role: "Crypto Investor",
  },
];

const faqs = [
  {
    q: "How do I install the indicator on TradingView?",
    a: "After purchase, you'll receive an invite to the indicator on TradingView. Simply accept the invite and add it to your chart from the Indicators menu under 'Invite-only scripts'.",
  },
  {
    q: "Does the indicator work on all timeframes?",
    a: "While the indicator works on all timeframes, it is specifically optimized for the 15-minute chart on BTC and XAU pairs for the highest accuracy signals.",
  },
  {
    q: "Can I use it on multiple devices?",
    a: "Yes. Since Confluence runs on TradingView, you can access it from any device where you're logged into your TradingView account.",
  },
  {
    q: "Is there a refund policy?",
    a: "We offer a 7-day refund policy. If you're not satisfied with the indicator, contact our support team within 7 days of purchase for a full refund.",
  },
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="glass-card rounded-xl border border-white/5 overflow-hidden cursor-pointer"
      onClick={() => setOpen(!open)}
      data-testid={`faq-item-${faq.q.slice(0, 20).replace(/\s/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center justify-between p-5">
        <span className="font-heading font-bold text-sm sm:text-base pr-4">{faq.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-primary flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </div>
      {open && (
        <div className="px-5 pb-5 -mt-1">
          <p className="text-sm text-muted-foreground">{faq.a}</p>
        </div>
      )}
    </div>
  );
}

export default function Confluence() {
  const [annual, setAnnual] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <NavBar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-[150px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">TradingView Indicator</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black mb-6">
              Trade with{" "}
              <span className="text-primary">Institutional</span> Precision
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              The Confluence Indicator. Where momentum meets volume. Identify
              high-probability setups on BTC and XAU charts with
              institutional-grade signals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/register">
                <Button
                  size="lg"
                  className="neon-glow font-heading font-bold uppercase tracking-wider"
                  data-testid="confluence-get-access"
                >
                  Get Access <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#features">
                <Button
                  size="lg"
                  variant="outline"
                  className="font-heading font-bold uppercase tracking-wider border-white/20 hover:bg-white/5"
                  data-testid="confluence-learn-more"
                >
                  Learn More
                </Button>
              </a>
            </div>

            {/* Social proof */}
            <div className="flex items-center justify-center gap-3">
              <div className="flex -space-x-3">
                {[
                  "https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=80&h=80&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1543132220-7bc04a0e790a?w=80&h=80&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1655249481446-25d575f1c054?w=80&h=80&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1530281834572-02d15fa61f64?w=80&h=80&fit=crop&crop=face",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Trader ${i + 1}`}
                    className="w-10 h-10 rounded-full border-2 border-background object-cover"
                  />
                ))}
              </div>
              <div className="text-left">
                <p className="text-sm font-heading font-bold">500+ Active Traders</p>
                <p className="text-xs text-muted-foreground">Using Confluence</p>
              </div>
            </div>
          </motion.div>

          {/* Chart preview card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 max-w-3xl mx-auto"
          >
            <div className="glass-card rounded-2xl p-1 border-primary/20">
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/7108129/pexels-photo-7108129.jpeg"
                  alt="Confluence Indicator Chart"
                  className="w-full h-64 sm:h-80 object-cover"
                  data-testid="confluence-chart-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-3">
                  <div className="px-3 py-1.5 rounded-lg bg-primary/20 border border-primary/30 backdrop-blur-sm">
                    <span className="text-xs font-heading font-bold text-primary">Signal Detected</span>
                  </div>
                  <span className="text-xs text-muted-foreground">BTC/USDT &bull; XAU/USD</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Features</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-3 mb-4">
              Everything You Need to{" "}
              <span className="text-primary">Trade Smarter</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`feature-card-${i}`}
                  className="glass-card rounded-xl p-6 hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-3 mb-6">
              Choose Your <span className="text-primary">Plan</span>
            </h2>

            {/* Toggle */}
            <div
              className="inline-flex items-center rounded-full bg-white/5 border border-white/10 p-1"
              data-testid="pricing-toggle"
            >
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  !annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  annual ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Annual (Save 17%)
              </button>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {/* Monthly */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="pricing-monthly"
              className={`glass-card rounded-2xl p-8 transition-all ${
                !annual ? "border-primary/40 ring-1 ring-primary/20" : "border-white/5"
              }`}
            >
              <h3 className="font-heading font-bold text-xl mb-1">Monthly</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Perfect for trying out Confluence
              </p>
              <div className="mb-6">
                <span className="text-4xl font-heading font-black">$49.99</span>
                <span className="text-muted-foreground text-sm">/per month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Full Indicator Access
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Email Support
                </li>
              </ul>
              <Link to="/register">
                <Button
                  className="w-full font-heading font-bold"
                  variant={!annual ? "default" : "outline"}
                  data-testid="pricing-monthly-cta"
                >
                  Get Started
                </Button>
              </Link>
            </motion.div>

            {/* Annual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="pricing-annual"
              className={`glass-card rounded-2xl p-8 relative transition-all ${
                annual ? "border-primary/40 ring-1 ring-primary/20" : "border-white/5"
              }`}
            >
              <div className="absolute -top-3 right-6">
                <span className="px-3 py-1 text-xs font-medium bg-primary/20 text-primary rounded-full border border-primary/30">
                  Best Value
                </span>
              </div>
              <h3 className="font-heading font-bold text-xl mb-1">Annual</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Best value for serious traders
              </p>
              <div className="mb-6">
                <span className="text-4xl font-heading font-black">$299</span>
                <span className="text-muted-foreground text-sm">/per year</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Full Indicator Access
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Annual Updates
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Priority Support
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" /> Early Access to New Features
                </li>
              </ul>
              <Link to="/register">
                <Button
                  className="w-full neon-glow font-heading font-bold"
                  data-testid="pricing-annual-cta"
                >
                  Get Started
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 relative bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-3 mb-4">
              Trusted by <span className="text-primary">500+</span> Traders
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`testimonial-${i}`}
                className="glass-card rounded-xl p-6 flex flex-col"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-6 flex-grow italic">
                  "{t.quote}"
                </p>
                <div>
                  <p className="font-heading font-bold text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-3 mb-4">
              Frequently Asked <span className="text-primary">Questions</span>
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.q} faq={faq} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
